// packages/shared/src/client-env.ts
import { z } from "zod";
var clientSecurityStates = ["warning", "blocked"];
var clientSecurityStateSchema = z.enum(clientSecurityStates);
var clientEnvSchema = z.object({
  /** Where the immutable client assets live. The one variable that repoints the whole client. */
  assetPath: z.string().min(1),
  /** Opaque SHA-256 fingerprint of the canonical asset path, for diagnostic correlation only. */
  assetPathId: z.string().regex(/^sha256:[a-f0-9]{64}$/),
  /** Same-origin path the proxy is mounted at, so there is no CORS and no cookie problem. */
  proxyPath: z.string().min(1),
  /** Which board this is, resolved server-side so the client parses no URLs. */
  board: z.string().min(1),
  /** Omitted entirely when unauthenticated — presence is what turns auth on. */
  auth: z.object({
    issuer: z.string().min(1),
    clientId: z.string().min(1),
    audience: z.string().min(1)
  }).optional(),
  /** Public deployment posture; it contains no board, source, or credential details. */
  security: clientSecurityStateSchema.optional()
});
var clientIdentityResponseSchema = clientEnvSchema.pick({ assetPath: true, assetPathId: true }).extend({ serverVersion: z.string().min(1) });
function readClientEnv(source = globalThis.window?.env) {
  const result = clientEnvSchema.safeParse(source);
  if (!result.success) {
    throw new Error(
      `window.env is missing or invalid \u2014 the server did not inject usable configuration.
${z.prettifyError(result.error)}`
    );
  }
  return result.data;
}

// packages/shared/src/duration.ts
import { z as z2 } from "zod";
var DURATION_PATTERN = /^([1-9][0-9]*)(ms|s|m|h)$/;
var UNIT_MILLIS = {
  ms: 1,
  s: 1e3,
  m: 6e4,
  h: 36e5
};
function parseDuration(value) {
  const match = DURATION_PATTERN.exec(value);
  if (!match) return null;
  const [, rawAmount, rawUnit] = match;
  if (rawAmount === void 0 || rawUnit === void 0) return null;
  const amount = Number(rawAmount);
  if (amount <= 0) return null;
  return amount * UNIT_MILLIS[rawUnit];
}
var durationSchema = z2.string().regex(/^[1-9][0-9]*(?:ms|s|m|h)$/, {
  message: 'must be a positive duration like "30s", "5m", or "1h"'
}).brand();

// packages/shared/src/envelope.ts
import { z as z3 } from "zod";
var errorKindSchema = z3.enum([
  "unreachable",
  "unauthorized",
  "not-found",
  "no-runs",
  "upstream-error"
]);
var envelopeErrorSchema = z3.object({
  kind: errorKindSchema,
  message: z3.string()
});
var envelopeSchema = z3.discriminatedUnion("state", [
  z3.object({
    panelId: z3.string().min(1),
    state: z3.literal("ok"),
    /** From the upstream response `Date`. A cached value must never be shown without its age. */
    observedAt: z3.iso.datetime(),
    /** Adapter-derived. The authority for this panel; the dashboard itself asserts nothing. */
    link: z3.url().nullable(),
    signal: z3.unknown()
  }),
  z3.object({
    panelId: z3.string().min(1),
    state: z3.literal("error"),
    /**
     * Populated even on error: knowing *when* a failure was observed is the difference between
     * "broken just now" and "broken all morning", and a panel that can't reach its source is
     * exactly when a viewer most wants to click through.
     */
    observedAt: z3.iso.datetime(),
    link: z3.url().nullable(),
    error: envelopeErrorSchema
  })
]);
var pipelineActivitySchema = z3.object({
  kind: z3.enum(["job", "stage", "step"]),
  name: z3.string().min(1),
  parent: z3.string().min(1).optional()
});
var pipelineStatusSchema = z3.object({
  type: z3.literal("pipeline-status"),
  status: z3.enum(["passed", "failed", "warning", "running", "cancelled", "unknown"]),
  /** The source's unmodified status/result vocabulary, for an honest display. */
  rawStatus: z3.string(),
  name: z3.string().min(1),
  /** The branch represented by this status, when the upstream supplies or filters by one. */
  branch: z3.string().min(1).optional(),
  /** Elapsed execution time, supplied only after the upstream run has completed. */
  durationMs: z3.number().int().nonnegative().optional(),
  /** When an active run began, so the client can advance its elapsed display between polls. */
  runStartedAt: z3.iso.datetime().optional(),
  /** Stable source-run identity, when the adapter provides one. */
  sourceRunId: z3.string().min(1).optional(),
  /** Best-effort live work detail; adapters may describe a job, stage, or step. */
  activity: pipelineActivitySchema.optional(),
  /** Advisory duration from recent completed runs remembered by this browser. */
  estimatedDurationMs: z3.number().int().positive().optional(),
  /** When this workflow run was last updated by its source, distinct from our observation time. */
  sourceUpdatedAt: z3.iso.datetime().optional()
});
var pipelineStatusPriorities = {
  passed: 0,
  cancelled: 1,
  unknown: 2,
  running: 3,
  warning: 4,
  failed: 5
};
function pipelineStatusPriority(status) {
  return pipelineStatusPriorities[status];
}
var pullRequestHealthItemSchema = z3.object({
  label: z3.string().min(1),
  status: pipelineStatusSchema.shape.status,
  detail: z3.string().min(1),
  link: z3.url().nullable()
});
var pullRequestHealthSchema = z3.object({
  type: z3.literal("pull-request-health"),
  status: pipelineStatusSchema.shape.status,
  summary: z3.string().min(1),
  workflows: z3.array(pullRequestHealthItemSchema),
  pullRequests: z3.array(pullRequestHealthItemSchema),
  /** Missing component reads remain visible even when the evidence we did get is reassuring. */
  incompleteObservations: z3.array(z3.object({ label: z3.string().min(1), message: z3.string().min(1) })).optional(),
  /** The latest component reading, disclosed beside the oldest aggregate evidence. */
  newestObservedAt: z3.iso.datetime().optional()
});
var pullRequestCandidateSchema = z3.object({
  number: z3.number().int().positive(),
  branch: z3.string().min(1),
  link: z3.url()
});
var pullRequestCandidatesSchema = z3.object({
  type: z3.literal("pull-request-candidates"),
  pullRequests: z3.array(pullRequestCandidateSchema),
  /** GitHub's first page is a deliberate bounded observation, not proof that no later PR exists. */
  truncated: z3.boolean().optional()
});
var pullRequestWorkflowObservationSchema = z3.object({
  type: z3.literal("pull-request-workflow"),
  workflow: z3.string().min(1),
  item: pullRequestHealthItemSchema
});
var pullRequestBuildObservationSchema = z3.object({
  type: z3.literal("pull-request-build"),
  branch: z3.string().min(1),
  item: pullRequestHealthItemSchema
});
var httpValueSchema = z3.object({
  type: z3.literal("http-value"),
  value: z3.union([z3.string(), z3.number(), z3.boolean()])
});

// packages/shared/src/layout.ts
var columnCount = 12;
var rowCount = 12;
function analyzeBoardLayout(panels) {
  const issues = panels.flatMap((panel) => {
    if (!panel.position || isZeroPosition(panel.position) || isPositionInCanvas(panel.position))
      return [];
    return [
      {
        panelId: panel.id,
        kind: "out-of-bounds",
        position: panel.position,
        conflictsWith: []
      }
    ];
  });
  const overlapIssues = /* @__PURE__ */ new Map();
  for (let leftIndex = 0; leftIndex < panels.length; leftIndex++) {
    const left = panels[leftIndex];
    if (!left?.position || isZeroPosition(left.position)) continue;
    for (let rightIndex = leftIndex + 1; rightIndex < panels.length; rightIndex++) {
      const right = panels[rightIndex];
      if (!right?.position || isZeroPosition(right.position)) continue;
      if (!overlaps(left.position, right.position)) continue;
      const key = `${right.id}\0overlap`;
      const issue = overlapIssues.get(key);
      if (issue) issue.conflictsWith.push(left.id);
      else {
        const nextIssue = {
          panelId: right.id,
          kind: "overlap",
          position: right.position,
          conflictsWith: [left.id]
        };
        overlapIssues.set(key, nextIssue);
        issues.push(nextIssue);
      }
    }
  }
  return { issues };
}
function normalizeBoardLayout(panels) {
  const positioned = panels.flatMap((panel) => {
    if (!panel.position || isZeroPosition(panel.position)) return [];
    return [{ ...panel.position, panelId: panel.id }];
  });
  if (positioned.length === 0) return panels.map((panel) => ({ ...panel }));
  const minX = Math.min(...positioned.map((rectangle) => rectangle.x));
  const minY = Math.min(...positioned.map((rectangle) => rectangle.y));
  const maxX = Math.max(...positioned.map((rectangle) => rectangle.x + rectangle.w));
  const maxY = Math.max(...positioned.map((rectangle) => rectangle.y + rectangle.h));
  const scaleX = maxX - minX > columnCount ? columnCount / (maxX - minX) : 1;
  const scaleY = maxY - minY > rowCount ? rowCount / (maxY - minY) : 1;
  const accepted = [];
  return panels.map((panel) => {
    const position = panel.position;
    if (!position || isZeroPosition(position)) return { ...panel };
    const desired = scaleRectangle(position, minX, minY, scaleX, scaleY);
    const legal = nearestLegalRectangle(desired, accepted);
    if (!legal) return { ...panel, position: zeroPosition };
    accepted.push({ ...legal, panelId: panel.id });
    return { ...panel, position: legal };
  });
}
function isZeroPosition(position) {
  return position?.x === 0 && position.y === 0 && position.w === 0 && position.h === 0;
}
var zeroPosition = { x: 0, y: 0, w: 0, h: 0 };
function isPositionInCanvas(position) {
  return !isZeroPosition(position) && position.x + position.w <= columnCount && position.y + position.h <= rowCount;
}
function overlaps(left, right) {
  return left.x < right.x + right.w && left.x + left.w > right.x && left.y < right.y + right.h && left.y + left.h > right.y;
}
function scaleRectangle(position, minX, minY, scaleX, scaleY) {
  const x = scaleCoordinate(position.x, minX, scaleX);
  const y = scaleCoordinate(position.y, minY, scaleY);
  const right = scaleCoordinate(position.x + position.w, minX, scaleX);
  const bottom = scaleCoordinate(position.y + position.h, minY, scaleY);
  return {
    x,
    y,
    w: Math.max(1, right - x),
    h: Math.max(1, bottom - y)
  };
}
function scaleCoordinate(value, origin, scale) {
  return Math.round((value - origin) * scale);
}
function nearestLegalRectangle(desired, accepted) {
  const candidates = [];
  for (let width = desired.w; width >= 1; width--) {
    for (let height = desired.h; height >= 1; height--) {
      const shrink = desired.w - width + (desired.h - height);
      for (let y = 0; y <= rowCount - height; y++) {
        for (let x = 0; x <= columnCount - width; x++) {
          const candidate2 = { x, y, w: width, h: height };
          if (accepted.some((rectangle) => overlaps(candidate2, rectangle))) continue;
          candidates.push({
            ...candidate2,
            shrink,
            distance: Math.abs(x - desired.x) + Math.abs(y - desired.y)
          });
        }
      }
    }
  }
  candidates.sort(
    (left, right) => left.shrink - right.shrink || left.distance - right.distance || left.y - right.y || left.x - right.x
  );
  const [candidate] = candidates;
  if (!candidate) return void 0;
  return { x: candidate.x, y: candidate.y, w: candidate.w, h: candidate.h };
}

// packages/shared/src/polling-policy.ts
var pollingDefaults = {
  refreshMillis: 6e4,
  runningRefreshMillis: 15e3,
  runningCompletionRefreshMillis: 5e3,
  runningCompletionWindowMillis: 12e4
};
function resolvePollingSettings(board, panel) {
  const duration = (value, fallback) => parseDuration(value ?? "") ?? fallback;
  const panelValue = (key, boardValue, fallback) => duration(panel[key], duration(board[boardValue], fallback));
  return {
    refreshMillis: panelValue("refresh", "refresh", pollingDefaults.refreshMillis),
    runningRefreshMillis: panelValue(
      "running_refresh",
      "running_refresh",
      pollingDefaults.runningRefreshMillis
    ),
    runningCompletionRefreshMillis: panelValue(
      "running_completion_refresh",
      "running_completion_refresh",
      pollingDefaults.runningCompletionRefreshMillis
    ),
    runningCompletionWindowMillis: panelValue(
      "running_completion_window",
      "running_completion_window",
      pollingDefaults.runningCompletionWindowMillis
    )
  };
}

// packages/shared/src/running-animations.ts
var visibleRunningAnimations = [
  "radial",
  "runway",
  "orbit",
  "signal-field",
  "telemetry-bloom",
  "release-transit",
  "status-weather",
  "falling-shapes",
  "snowman"
];
export {
  analyzeBoardLayout,
  clientEnvSchema,
  clientIdentityResponseSchema,
  clientSecurityStateSchema,
  clientSecurityStates,
  durationSchema,
  envelopeSchema,
  errorKindSchema,
  httpValueSchema,
  isZeroPosition,
  normalizeBoardLayout,
  parseDuration,
  pipelineActivitySchema,
  pipelineStatusPriority,
  pipelineStatusSchema,
  pollingDefaults,
  pullRequestBuildObservationSchema,
  pullRequestCandidateSchema,
  pullRequestCandidatesSchema,
  pullRequestHealthSchema,
  pullRequestWorkflowObservationSchema,
  readClientEnv,
  resolvePollingSettings,
  visibleRunningAnimations
};
